// src/utils/consult/peerMesh.ts
//
// The ONE WebRTC negotiation core both consult peers use (practitioner VideoConsultation and the
// patient Consultation page). The two pages used to carry near-copies of this logic and the copies
// had already diverged — one handled `disconnected`, the other did not, and both had the same three
// production defects this module exists to close:
//
//   1. `restartIce()` was called with no `onnegotiationneeded` handler. restartIce only flags the
//      ICE agent and queues a negotiationneeded event; with nobody listening, NO offer followed and
//      a dropped call could never re-establish. Here every offer — first connect, track upgrade and
//      ICE restart alike — flows from the one `negotiate()` path that negotiationneeded drives.
//
//   2. Local tracks were only added at RTCPeerConnection creation. On a practitioner refresh the
//      remote offer arrives while getUserMedia is still pending, so the answer went out recvonly
//      and nothing ever added the tracks: the practitioner saw the patient, the patient got
//      nothing. `attachLocalTracksToAll()` is the recovery — addTrack reuses the recvonly
//      transceivers created below and negotiationneeded renegotiates to sendrecv.
//
//   3. Glare was "handled" by BOTH sides rolling back — under a real offer collision each side then
//      answers the other's offer and drops the other's answer, leaving two peers on two different
//      sessions. This is the standard perfect-negotiation pattern instead: the peer with the LOWER
//      socket id is polite (deterministic and symmetric — both ends compare the same pair), the
//      impolite side ignores a colliding offer, and setRemoteDescription performs the implicit
//      rollback on the polite side.
//
// The wire contract is unchanged: `videoSignal { roomId, type: offer|answer|ice-candidate, payload,
// targetSocketId }` relayed by talk, `offerRequested` inbound, `requestOffers` outbound as the
// teardown-recovery fallback. talk needed no change for any of this.
//
// Logging follows the pages' console style — event-shaped, socket ids and room id only, never SDP
// bodies and never patient data.
import { iceServersFromEnv } from './iceServers';

export interface PeerMeshOptions {
	roomId: string;
	/** Short tag for console lines, e.g. "Practitioner" | "Patient". */
	logTag: string;
	getSocket: () => { emit: (event: string, data: any) => void } | null | undefined;
	getMySocketId: () => string;
	getLocalStream: () => MediaStream | null;
	onRemoteStream: (peerSocketId: string, stream: MediaStream) => void;
	/** The peer's connection is gone (left, replaced, or recovery teardown) — drop its tile. */
	onPeerClosed: (peerSocketId: string) => void;
	onConnectionStateChange?: (peerSocketId: string, state: RTCPeerConnectionState) => void;
	rtcConfiguration?: RTCConfiguration;
	/** How long `disconnected` may sit before an ICE restart is attempted. */
	disconnectedGraceMs?: number;
	/** How long after `failed` (and its immediate ICE restart) before full teardown + requestOffers. */
	failedRecoveryMs?: number;
}

export interface PeerMesh {
	handleVideoSignal(data: { type?: string; payload?: any; fromSocketId?: string }): Promise<void>;
	handleOfferRequested(requesterSocketId: string): Promise<void>;
	/** Add any local tracks that are not yet being sent, on every peer — renegotiates via onnegotiationneeded. */
	attachLocalTracksToAll(): void;
	/** Tear down every peer when our own socket id changed (reconnect/rejoin). Returns true if it did. */
	resetIfSocketIdChanged(): boolean;
	closePeer(peerSocketId: string): void;
	closeAll(): void;
	forEachPc(callback: (pc: RTCPeerConnection, peerSocketId: string) => void): void;
	peerCount(): number;
}

interface PeerState {
	pc: RTCPeerConnection;
	polite: boolean;
	makingOffer: boolean;
	ignoreOffer: boolean;
	isSettingRemoteAnswerPending: boolean;
	disconnectTimer: ReturnType<typeof setTimeout> | null;
	failedTimer: ReturnType<typeof setTimeout> | null;
	everConnected: boolean;
}

const REUSABLE_STATES = ['new', 'connecting', 'connected'];

// host|srflx|prflx|relay. Never the candidate string — it carries the peer's IP (root CLAUDE.md §9).
const candidateType = (candidate: any): string => {
	if (candidate?.type) return String(candidate.type);
	const match = /\btyp (\w+)/.exec(String(candidate?.candidate ?? ''));
	return match ? match[1] : 'unknown';
};

export function createPeerMesh(options: PeerMeshOptions): PeerMesh {
	const {
		roomId,
		logTag,
		getSocket,
		getMySocketId,
		getLocalStream,
		onRemoteStream,
		onPeerClosed,
		onConnectionStateChange,
		disconnectedGraceMs = 2000,
		failedRecoveryMs = 7000,
	} = options;

	const peers = new Map<string, PeerState>();
	// Candidates that arrived before the peer's remote description (or before the peer exists at
	// all). Kept outside PeerState so an offer can collect candidates that outran it, and cleared
	// whenever the peer's session is torn down so a stale generation never flushes into a fresh one.
	const pendingCandidates = new Map<string, RTCIceCandidateInit[]>();
	// Recovery-teardown count per peer since it last reached `connected` — two or more without ever
	// connecting is the signature of a path that needs a TURN relay (out of scope this pass).
	const recoveryTeardowns = new Map<string, number>();
	let lastSocketId = getMySocketId();

	const log = (event: string, detail: Record<string, unknown> = {}) =>
		console.log(`📹 [${logTag}] ${event}`, { roomId, ...detail });

	const emitSignal = (type: 'offer' | 'answer' | 'ice-candidate', payload: any, targetSocketId: string) => {
		const socket = getSocket();
		if (!socket) return;
		socket.emit('videoSignal', { roomId, type, payload, targetSocketId });
	};

	const clearTimers = (state: PeerState) => {
		if (state.disconnectTimer) {
			clearTimeout(state.disconnectTimer);
			state.disconnectTimer = null;
		}
		if (state.failedTimer) {
			clearTimeout(state.failedTimer);
			state.failedTimer = null;
		}
	};

	const closePeer = (peerSocketId: string) => {
		pendingCandidates.delete(peerSocketId);
		const state = peers.get(peerSocketId);
		if (!state) return;
		clearTimers(state);
		peers.delete(peerSocketId);
		try {
			state.pc.close();
		} catch {
			// already closed
		}
		onPeerClosed(peerSocketId);
	};

	const closeAll = () => {
		for (const peerSocketId of Array.from(peers.keys())) {
			closePeer(peerSocketId);
		}
		pendingCandidates.clear();
	};

	const attachMissingLocalTracks = (pc: RTCPeerConnection) => {
		const stream = getLocalStream();
		if (!stream) return;
		for (const track of stream.getTracks()) {
			// One sender per kind: screen share replaces the video sender's track, so "a sender of
			// this kind is already sending" must not be re-added — and a never-sent recvonly
			// transceiver (created below when media was not ready) is what addTrack upgrades.
			const alreadySending = pc.getSenders().some((sender) => sender.track && sender.track.kind === track.kind);
			if (!alreadySending) {
				pc.addTrack(track, stream);
			}
		}
	};

	/** All offers — initial, upgrade and ICE restart — leave through here, guarded per peer. */
	const negotiate = async (peerSocketId: string, state: PeerState) => {
		if (state.makingOffer || state.pc.signalingState !== 'stable') return;
		state.makingOffer = true;
		try {
			await state.pc.setLocalDescription();
			emitSignal('offer', state.pc.localDescription, peerSocketId);
			log('webrtc.offer.sent', { peerSocketId });
		} catch (error) {
			console.error(`📹 [${logTag}] webrtc.offer.failed`, { roomId, peerSocketId });
		} finally {
			state.makingOffer = false;
		}
	};

	const recoverPeer = (peerSocketId: string) => {
		const attempts = (recoveryTeardowns.get(peerSocketId) || 0) + 1;
		recoveryTeardowns.set(peerSocketId, attempts);
		console.warn(`📹 [${logTag}] webrtc.recovery.teardown`, { roomId, peerSocketId, attempts });
		if (attempts >= 2 && !peers.get(peerSocketId)?.everConnected) {
			// Direct + STUN keep failing outright: this pairing almost certainly needs a TURN relay,
			// which is the documented follow-up, not something this pass can fix.
			console.warn(`📹 [${logTag}] webrtc.relay_required_suspected`, { roomId, peerSocketId });
		}
		closePeer(peerSocketId);
		const socket = getSocket();
		if (socket) {
			// The room's other participants answer with fresh offers (talk's requestOffers fan-out),
			// which rebuilds the pair from scratch — the recovery of last resort when an in-place ICE
			// restart could not bring the transport back.
			socket.emit('requestOffers', { roomId });
		}
	};

	const createPeer = (peerSocketId: string): PeerState => {
		log('webrtc.peer.created', { peerSocketId });
		const pc = new RTCPeerConnection(
			options.rtcConfiguration ?? {
				iceServers: iceServersFromEnv(),
				iceCandidatePoolSize: 10,
				bundlePolicy: 'max-bundle',
				rtcpMuxPolicy: 'require',
			},
		);

		const state: PeerState = {
			pc,
			// Deterministic and symmetric: both ends compare the same id pair, so exactly one side is
			// polite. Recomputed whenever a peer is (re)created, which covers socket-id changes.
			polite: getMySocketId() < peerSocketId,
			makingOffer: false,
			ignoreOffer: false,
			isSettingRemoteAnswerPending: false,
			disconnectTimer: null,
			failedTimer: null,
			everConnected: false,
		};
		peers.set(peerSocketId, state);

		const stream = getLocalStream();
		if (stream) {
			stream.getTracks().forEach((track) => pc.addTrack(track, stream));
		} else {
			// Media not ready yet (the refresh race): reserve one m-line per kind so the answer is
			// well-formed recvonly NOW, and the later addTrack in attachLocalTracksToAll upgrades
			// these very transceivers to sendrecv instead of growing the SDP.
			pc.addTransceiver('audio', { direction: 'recvonly' });
			pc.addTransceiver('video', { direction: 'recvonly' });
		}

		pc.onnegotiationneeded = () => {
			void negotiate(peerSocketId, state);
		};

		pc.ontrack = (event) => {
			log('webrtc.track.received', { peerSocketId, kind: event.track.kind, streams: event.streams?.length ?? 0 });
			// A track with no msid arrives with an empty streams list; dropping it is a black screen on a `connected` peer.
			onRemoteStream(peerSocketId, event.streams?.[0] ?? new MediaStream([event.track]));
		};

		pc.onicecandidate = (event) => {
			if (event.candidate) {
				log('webrtc.candidate.gathered', { peerSocketId, candidateType: candidateType(event.candidate) });
				emitSignal('ice-candidate', event.candidate, peerSocketId);
			}
		};

		pc.onconnectionstatechange = () => {
			const connectionState = pc.connectionState;
			log('webrtc.connection_state', { peerSocketId, connectionState });
			onConnectionStateChange?.(peerSocketId, connectionState);

			if (connectionState === 'connected') {
				state.everConnected = true;
				recoveryTeardowns.delete(peerSocketId);
				clearTimers(state);
			} else if (connectionState === 'disconnected') {
				// Often transient (a route change, a wifi blip) — give the ICE agent a grace period
				// to come back on its own before forcing a restart.
				if (!state.disconnectTimer) {
					state.disconnectTimer = setTimeout(() => {
						state.disconnectTimer = null;
						if (pc.connectionState === 'disconnected') {
							log('webrtc.ice_restart', { peerSocketId, reason: 'disconnected_grace_elapsed' });
							pc.restartIce();
						}
					}, disconnectedGraceMs);
				}
			} else if (connectionState === 'failed') {
				clearTimers(state);
				// restartIce fires negotiationneeded, so — unlike before — an offer actually follows.
				log('webrtc.ice_restart', { peerSocketId, reason: 'failed' });
				pc.restartIce();
				state.failedTimer = setTimeout(() => {
					state.failedTimer = null;
					if (pc.connectionState !== 'connected') {
						recoverPeer(peerSocketId);
					}
				}, failedRecoveryMs);
			} else if (connectionState === 'closed') {
				closePeer(peerSocketId);
			}
		};

		pc.oniceconnectionstatechange = () => log('webrtc.ice_state', { peerSocketId, iceConnectionState: pc.iceConnectionState });

		return state;
	};

	// For the paths where WE are about to offer (offerRequested, ensurePeer): the requester holds
	// no connection for us — it just joined, or tore its half down and asked the room for fresh
	// offers — so a local peer in `disconnected`/`failed` is the stale half of that dead pair and
	// is rebuilt (its candidate buffer dropped with it) rather than offered from. The INBOUND offer
	// path deliberately does NOT use this: see handleVideoSignal's offer case.
	const ensurePeerState = (peerSocketId: string): PeerState => {
		const existing = peers.get(peerSocketId);
		if (existing && REUSABLE_STATES.includes(existing.pc.connectionState)) {
			return existing;
		}
		if (existing) {
			log('webrtc.peer.rebuilt', { peerSocketId, connectionState: existing.pc.connectionState });
			closePeer(peerSocketId);
		}
		return createPeer(peerSocketId);
	};

	const flushPendingCandidates = async (peerSocketId: string, state: PeerState) => {
		const queued = pendingCandidates.get(peerSocketId);
		if (!queued || !state.pc.remoteDescription) return;
		pendingCandidates.delete(peerSocketId);
		for (const candidate of queued) {
			try {
				await state.pc.addIceCandidate(candidate);
			} catch (error) {
				if (!state.ignoreOffer) {
					console.error(`📹 [${logTag}] webrtc.candidate.buffered_add_failed`, { roomId, peerSocketId, candidateType: candidateType(candidate) });
				}
			}
		}
	};

	const handleVideoSignal = async (data: { type?: string; payload?: any; fromSocketId?: string }) => {
		const { type, payload, fromSocketId } = data || {};
		if (!fromSocketId || fromSocketId === getMySocketId()) return;

		try {
			switch (type) {
				case 'offer': {
					// An offer for a peer we already track is a renegotiation on the SAME pairing, so
					// the entry is reused WHATEVER its connectionState: an ICE-restart offer arrives
					// exactly when the connection is disconnected/failed, and the dead-state rebuild
					// ensurePeerState does would destroy the very connection the restart exists to
					// recover. A genuinely fresh remote peer never reaches this path with a live entry
					// — a refresh/reconnect changes the socket-id key, and the recovery teardown
					// (recoverPeer → requestOffers) clears both ends before offers flow again.
					const state = peers.get(fromSocketId) ?? createPeer(fromSocketId);
					const pc = state.pc;
					// Perfect negotiation: a collision is an offer landing while we have one of our
					// own in flight. The impolite side ignores it (its own offer stands and the
					// polite side will answer that one); the polite side lets setRemoteDescription
					// roll its own offer back and answers.
					const readyForOffer = !state.makingOffer && (pc.signalingState === 'stable' || state.isSettingRemoteAnswerPending);
					const collision = !readyForOffer;
					state.ignoreOffer = !state.polite && collision;
					if (state.ignoreOffer) {
						log('webrtc.offer.ignored_glare', { peerSocketId: fromSocketId });
						return;
					}
					await pc.setRemoteDescription(payload);
					await flushPendingCandidates(fromSocketId, state);
					await pc.setLocalDescription();
					emitSignal('answer', pc.localDescription, fromSocketId);
					log('webrtc.answer.sent', { peerSocketId: fromSocketId });
					break;
				}
				case 'answer': {
					const state = peers.get(fromSocketId);
					if (!state) return;
					if (state.pc.signalingState !== 'have-local-offer') {
						// A late answer to an offer that no longer exists (rolled back or rebuilt).
						log('webrtc.answer.stale_dropped', { peerSocketId: fromSocketId });
						return;
					}
					state.isSettingRemoteAnswerPending = true;
					try {
						await state.pc.setRemoteDescription(payload);
					} finally {
						state.isSettingRemoteAnswerPending = false;
					}
					// The glare is over; left set, this silences every later candidate failure on the peer.
					state.ignoreOffer = false;
					await flushPendingCandidates(fromSocketId, state);
					break;
				}
				case 'ice-candidate': {
					const state = peers.get(fromSocketId);
					if (!state || !state.pc.remoteDescription) {
						const queued = pendingCandidates.get(fromSocketId) || [];
						queued.push(payload);
						pendingCandidates.set(fromSocketId, queued);
						return;
					}
					try {
						await state.pc.addIceCandidate(payload);
					} catch (error) {
						// Candidates for an offer we deliberately ignored are expected to fail.
						if (!state.ignoreOffer) {
							console.error(`📹 [${logTag}] webrtc.candidate.add_failed`, { roomId, peerSocketId: fromSocketId, candidateType: candidateType(payload) });
						}
					}
					break;
				}
				default:
					break;
			}
		} catch (error) {
			console.error(`📹 [${logTag}] webrtc.signal.failed`, { roomId, peerSocketId: fromSocketId, signalType: type });
		}
	};

	const handleOfferRequested = async (requesterSocketId: string) => {
		if (!requesterSocketId || requesterSocketId === getMySocketId()) return;
		const state = ensurePeerState(requesterSocketId);
		// Creating the peer above already queued a negotiationneeded; calling negotiate directly
		// makes the offer deterministic (and the makingOffer/stable guard keeps it to exactly one).
		await negotiate(requesterSocketId, state);
	};

	return {
		handleVideoSignal,
		handleOfferRequested,
		attachLocalTracksToAll: () => {
			peers.forEach((state) => attachMissingLocalTracks(state.pc));
		},
		resetIfSocketIdChanged: () => {
			const currentId = getMySocketId();
			if (currentId === lastSocketId) return false;
			const hadPeers = peers.size > 0;
			lastSocketId = currentId;
			if (!hadPeers) return false;
			// Our socket id is every remote peer's key for us. After it changes, the room's other
			// participants close their old connections (participantLeft) and offer to the NEW id, so
			// anything we still hold is a stale half of a dead pair — rebuild from zero.
			log('webrtc.mesh.reset_on_socket_change');
			closeAll();
			return true;
		},
		closePeer,
		closeAll,
		forEachPc: (callback) => {
			peers.forEach((state, peerSocketId) => callback(state.pc, peerSocketId));
		},
		peerCount: () => peers.size,
	};
}
